import { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonLabel,
  IonBackButton,
  IonButtons,
  IonToast,
  IonSpinner,
  IonIcon,
} from '@ionic/react';
import { save, personOutline, callOutline, locationOutline, walletOutline } from 'ionicons/icons';
import { z } from 'zod';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import {
  useLegalEntity,
  useUpdateLegalEntity,
  useMyCandidateProfile,
  useSessionInfo,
} from '@/lib/api/hooks';
import './LegalEntityManagement.css';

const schema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  companyName: z.string().min(2, 'Company name is required').optional().or(z.literal('')),
  tradingName: z.string().optional(),
  email: z.string().email('Valid email required'),
  phone: z.string().optional(),
  physicalAddress: z
    .object({
      street: z.string().min(1, 'Street is required'),
      city: z.string().min(1, 'City is required'),
      province: z.string().min(1, 'Province is required'),
      postalCode: z.string().min(4, 'Postal code is required'),
      country: z.string().default('ZA'),
    })
    .optional(),
  bankAccount: z
    .object({
      bankName: z.string().min(1, 'Bank name is required'),
      accountNumber: z.string().min(1, 'Account number is required'),
      accountType: z.enum(['cheque', 'savings']),
      branchCode: z.string().min(1, 'Branch code is required'),
    })
    .optional(),
});

interface Form {
  firstName: string;
  lastName: string;
  companyName: string;
  tradingName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  branchCode: string;
}

const BANKS = ['ABSA', 'FNB', 'Standard Bank', 'Nedbank', 'Capitec', 'African Bank', 'Discovery Bank', 'TymeBank', 'Other'];

const PROVINCES = [
  'Eastern Cape', 'Free State', 'Gauteng', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape', 'Western Cape',
];

export function LegalEntityManagement() {
  const history = useHistory();
  const { id: routeId } = useParams<{ id?: string }>();

  const [legalEntityId, setLegalEntityId] = useState(routeId ?? '');

  const { data: candidateEnvelope } = useMyCandidateProfile();
  const candidateLegalEntityId = candidateEnvelope?.data?.legalEntity?.id ?? '';

  const { data: sessionEnvelope } = useSessionInfo();
  const tenantId = sessionEnvelope?.data?.currentTenant?.id ?? '';

  useEffect(() => {
    if (legalEntityId) return;
    if (candidateLegalEntityId) {
      setLegalEntityId(candidateLegalEntityId);
    }
  }, [legalEntityId, candidateLegalEntityId]);

  const [employerLeId, setEmployerLeId] = useState('');
  useEffect(() => {
    if (legalEntityId || employerLeId || !tenantId) return;
    const fetchEmployerLegalEntity = async () => {
      try {
        const { api } = await import('@/lib/api/client');
        const res = await api.get('/workseeking/employers', {
          params: { tenantId },
        }) as { data: Array<{ legalEntity?: { id: string } }> };
        const leId = res.data?.[0]?.legalEntity?.id;
        if (leId) setEmployerLeId(leId);
      } catch {
        // No employer profile found
      }
    };
    fetchEmployerLegalEntity();
  }, [legalEntityId, employerLeId, tenantId]);

  useEffect(() => {
    if (employerLeId && !legalEntityId) {
      setLegalEntityId(employerLeId);
    }
  }, [employerLeId, legalEntityId]);

  const { data: envelope, isLoading } = useLegalEntity(legalEntityId);
  const legalEntity = envelope?.data;
  const updateEntity = useUpdateLegalEntity();

  const [form, setForm] = useState<Form>({
    firstName: '',
    lastName: '',
    companyName: '',
    tradingName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'ZA',
    bankName: '',
    accountNumber: '',
    accountType: 'savings',
    branchCode: '',
  });

  const [initialized, setInitialized] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  useEffect(() => {
    if (legalEntity && !initialized) {
      setForm({
        firstName: legalEntity.firstName ?? '',
        lastName: legalEntity.lastName ?? '',
        companyName: legalEntity.companyName ?? '',
        tradingName: legalEntity.tradingName ?? '',
        email: legalEntity.email ?? '',
        phone: legalEntity.phone ?? '',
        street: legalEntity.physicalAddress?.street ?? '',
        city: legalEntity.physicalAddress?.city ?? '',
        province: legalEntity.physicalAddress?.province ?? '',
        postalCode: legalEntity.physicalAddress?.postalCode ?? '',
        country: legalEntity.physicalAddress?.country ?? 'ZA',
        bankName: legalEntity.bankAccount?.bankName ?? '',
        accountNumber: legalEntity.bankAccount?.accountNumber ?? '',
        accountType: legalEntity.bankAccount?.accountType ?? 'savings',
        branchCode: legalEntity.bankAccount?.branchCode ?? '',
      });
      setInitialized(true);
    }
  }, [legalEntity, initialized]);

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = () => {
    const r = schema.safeParse({
      firstName: form.firstName,
      lastName: form.lastName,
      companyName: form.companyName || undefined,
      tradingName: form.tradingName || undefined,
      email: form.email,
      phone: form.phone || undefined,
      physicalAddress: form.street
        ? { street: form.street, city: form.city, province: form.province, postalCode: form.postalCode, country: form.country }
        : undefined,
      bankAccount: form.bankName
        ? { bankName: form.bankName, accountNumber: form.accountNumber, accountType: form.accountType as 'cheque' | 'savings', branchCode: form.branchCode }
        : undefined,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const key = issue.path.join('.');
        e[key || 'root'] = issue.message;
      }
      setErrors(e);
      return;
    }
    setErrors({});

    if (!legalEntity?._id) {
      setToast({ msg: 'No legal entity found', color: 'danger' });
      return;
    }

    const payload: Record<string, unknown> = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
    };
    if (form.companyName) payload.companyName = form.companyName;
    if (form.tradingName) payload.tradingName = form.tradingName;
    if (form.phone) payload.phone = form.phone;
    if (form.street) {
      payload.physicalAddress = {
        street: form.street,
        city: form.city,
        province: form.province,
        postalCode: form.postalCode,
        country: form.country,
      };
    }
    if (form.bankName) {
      payload.bankAccount = {
        bankName: form.bankName,
        accountNumber: form.accountNumber,
        accountType: form.accountType,
        branchCode: form.branchCode,
      };
    }

    updateEntity.mutate(
      { id: legalEntity._id, data: payload },
      {
        onSuccess: () => {
          setToast({ msg: 'Details saved', color: 'success' });
          setTimeout(() => history.goBack(), 800);
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to save', color: 'danger' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <IonPage>
        <NoindexSeo />
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/profile" /></IonButtons>
            <IonTitle>Personal & Company Details</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding lem-page">
          <div className="lem-loading">
            <IonSpinner name="crescent" />
            <p className="lem-loading-text">Loading details...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/profile" /></IonButtons>
          <IonTitle>Personal & Company Details</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} disabled={updateEntity.isPending}>
              {updateEntity.isPending ? <IonSpinner name="crescent" style={{ width: 20, height: 20 }} /> : <IonIcon icon={save} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding lem-page">

        {/* Personal & Company Details */}
        <FormSection icon={personOutline} title="Personal & Company Details">
          <FormField label="First name" error={errors.firstName}>
            <IonInput
              placeholder="Enter first name"
              value={form.firstName}
              onIonInput={(e) => set({ firstName: e.detail.value! })}
            />
          </FormField>
          <FormField label="Last name" error={errors.lastName}>
            <IonInput
              placeholder="Enter last name"
              value={form.lastName}
              onIonInput={(e) => set({ lastName: e.detail.value! })}
            />
          </FormField>
          <FormField label="Company name" error={undefined}>
            <IonInput
              placeholder="Optional"
              value={form.companyName}
              onIonInput={(e) => set({ companyName: e.detail.value! })}
            />
          </FormField>
          <FormField label="Trading name" error={undefined}>
            <IonInput
              placeholder="Optional"
              value={form.tradingName}
              onIonInput={(e) => set({ tradingName: e.detail.value! })}
            />
          </FormField>
        </FormSection>

        {/* Contact */}
        <FormSection icon={callOutline} title="Contact">
          <FormField label="Email" error={errors.email}>
            <IonInput
              type="email"
              placeholder="email@example.com"
              value={form.email}
              onIonInput={(e) => set({ email: e.detail.value! })}
            />
          </FormField>
          <FormField label="Phone" error={undefined}>
            <IonInput
              placeholder="Optional"
              value={form.phone}
              onIonInput={(e) => set({ phone: e.detail.value! })}
            />
          </FormField>
        </FormSection>

        {/* Physical Address */}
        <FormSection icon={locationOutline} title="Physical Address">
          <FormField label="Street" error={errors['physicalAddress.street']}>
            <IonInput
              placeholder="Enter street address"
              value={form.street}
              onIonInput={(e) => set({ street: e.detail.value! })}
            />
          </FormField>
          <FormField label="City" error={errors['physicalAddress.city']}>
            <IonInput
              placeholder="Enter city"
              value={form.city}
              onIonInput={(e) => set({ city: e.detail.value! })}
            />
          </FormField>
          <FormField label="Province" error={errors['physicalAddress.province']}>
            <IonSelect value={form.province} onIonChange={(e) => set({ province: e.detail.value })}>
              <IonSelectOption value="">Select province</IonSelectOption>
              {PROVINCES.map((p) => <IonSelectOption key={p} value={p}>{p}</IonSelectOption>)}
            </IonSelect>
          </FormField>
          <FormField label="Postal code" error={errors['physicalAddress.postalCode']}>
            <IonInput
              placeholder="Enter postal code"
              value={form.postalCode}
              onIonInput={(e) => set({ postalCode: e.detail.value! })}
            />
          </FormField>
        </FormSection>

        {/* Bank Account */}
        <FormSection icon={walletOutline} title="Bank Account">
          <FormField label="Bank" error={errors['bankAccount.bankName']}>
            <IonSelect value={form.bankName} onIonChange={(e) => set({ bankName: e.detail.value })}>
              <IonSelectOption value="">Select bank</IonSelectOption>
              {BANKS.map((b) => <IonSelectOption key={b} value={b}>{b}</IonSelectOption>)}
            </IonSelect>
          </FormField>
          <FormField label="Account number" error={errors['bankAccount.accountNumber']}>
            <IonInput
              placeholder="Enter account number"
              value={form.accountNumber}
              onIonInput={(e) => set({ accountNumber: e.detail.value! })}
            />
          </FormField>
          <FormField label="Account type" error={undefined}>
            <IonSelect value={form.accountType} onIonChange={(e) => set({ accountType: e.detail.value })}>
              <IonSelectOption value="savings">Savings</IonSelectOption>
              <IonSelectOption value="cheque">Cheque / Current</IonSelectOption>
            </IonSelect>
          </FormField>
          <FormField label="Branch code" error={errors['bankAccount.branchCode']}>
            <IonInput
              placeholder="Enter branch code"
              value={form.branchCode}
              onIonInput={(e) => set({ branchCode: e.detail.value! })}
            />
          </FormField>
        </FormSection>

        {/* Save button */}
        <IonButton expand="block" className="lem-save-btn" onClick={handleSave} disabled={updateEntity.isPending}>
          {updateEntity.isPending ? 'Saving...' : 'Save details'}
        </IonButton>

        <AppFooter />
      </IonContent>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}

function FormSection({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="lem-section">
      <div className="lem-section-header">
        <IonIcon icon={icon} className="lem-section-icon" />
        <h3 className="lem-section-title">{title}</h3>
      </div>
      <div className="lem-section-body">
        {children}
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="lem-field">
      <IonLabel className="lem-field-label">{label}</IonLabel>
      {children}
      {error && <span className="lem-field-error">{error}</span>}
    </div>
  );
}
